import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import Logo from './Logo';
import { useLang, useT } from '../context/LanguageContext';
import { ui } from '../translations';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { lang, toggle } = useLang();
  const t = useT();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  const links = [
    { to: '/', label: t(ui.nav.home) },
    { to: '/despre', label: t(ui.nav.about) },
    { to: '/servicii', label: t(ui.nav.services) },
    { to: '/portofoliu', label: t(ui.nav.portfolio) },
    { to: '/contact', label: t(ui.nav.contact) },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'navbar-scrolled' : 'bg-white shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-18 py-3">
          <Link to="/" data-testid="nav-logo"><Logo variant="light" /></Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-8">
            {links.map(l => (
              <Link key={l.to} to={l.to} data-testid={`nav-link-${l.to.replace('/', '') || 'home'}`}
                className={`nav-link text-sm font-semibold uppercase tracking-wider transition-colors duration-200 ${isActive(l.to) ? 'text-[#0A192F] active' : 'text-slate-600 hover:text-[#0A192F]'}`}>
                {l.label}
              </Link>
            ))}
          </div>

          {/* Right controls */}
          <div className="hidden lg:flex items-center gap-4">
            <button data-testid="lang-toggle" onClick={toggle}
              className="flex items-center gap-1 text-sm font-bold border border-slate-200 rounded px-3 py-1.5 hover:border-[#00BCD4] transition-colors">
              <span className={lang === 'ro' ? 'text-[#0A192F]' : 'text-slate-400'}>RO</span>
              <span className="text-slate-300">|</span>
              <span className={lang === 'en' ? 'text-[#0A192F]' : 'text-slate-400'}>EN</span>
            </button>
            <Link to="/contact" data-testid="nav-cta"
              className="btn-primary text-sm">
              {t(ui.nav.getQuote)}
            </Link>
          </div>

          {/* Mobile controls */}
          <div className="flex lg:hidden items-center gap-3">
            <button data-testid="lang-toggle-mobile" onClick={toggle}
              className="text-xs font-bold border border-slate-200 rounded px-2 py-1">
              {lang.toUpperCase()}
            </button>
            <button data-testid="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 text-[#0A192F]">
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-100 shadow-lg">
          <div className="px-6 py-4 flex flex-col gap-4">
            {links.map(l => (
              <Link key={l.to} to={l.to}
                className={`text-sm font-semibold uppercase tracking-wider py-2 border-b border-slate-50 ${isActive(l.to) ? 'text-[#00BCD4]' : 'text-[#0A192F]'}`}>
                {l.label}
              </Link>
            ))}
            <Link to="/contact" className="btn-primary text-sm mt-2 justify-center">
              {t(ui.nav.getQuote)}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
